<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeMount, ref, watch } from 'vue';
import { useADialog } from '@Atoms';
import { useLazyGoogleMaps } from '@Composables';
import type { Field } from '../../../types';

const props = defineProps<{
  field?: Field;
}>();

const { activeModal, activeModalProps, dialogVisibility } = useADialog();

const address = computed(() => props.field?.settings?.address);

const coordinates = ref({
  lat: props.field?.settings?.coordinates?.length === 2 ? props.field.settings.coordinates[1] : 0,
  lng: props.field?.settings?.coordinates?.length === 2 ? props.field.settings.coordinates[0] : 0,
});

watch(
  () => props.field,
  () => applyNewCoordinates()
);

onBeforeMount(() => useLazyGoogleMaps(getCurrentInstance()?.appContext.app!));

function onOpenChangeLocationModal() {
  activeModalProps.value = {
    locationInfo: props.field?.settings?.modalProps,
    type: props.field?.settings?.type,
  };
  activeModal.value = props.field?.settings?.activeModal;
  dialogVisibility.value = true;
}

function onOpenDeleteLocationModal() {
  activeModal.value = props.field?.settings?.activeDeleteModal;
  activeModalProps.value = {
    infos: { id: props.field?.settings?.modalProps?.id, type: props.field?.settings?.type },
    message: 'animals.individualPage.location.delete',
  };
  dialogVisibility.value = true;
}

function applyNewCoordinates() {
  coordinates.value = {
    lat: props.field?.settings?.coordinates?.length === 2 ? props.field.settings.coordinates[1] : 0,
    lng: props.field?.settings?.coordinates?.length === 2 ? props.field.settings.coordinates[0] : 0,
  };
}
</script>

<template>
  <div
    v-if="address"
    class="group mt-2 flex gap-2"
  >
    <p
      v-tooltip.top="{ value: $t('components.forms.title.changeLocation'), showDelay: 1000 }"
      class="cursor-pointer text-sm"
      @click="onOpenChangeLocationModal"
    >
      {{ address }}
    </p>

    <div class="flex gap-4 opacity-0 group-hover:opacity-100">
      <i
        class="pi pi-pencil cursor-pointer text-sm transition-opacity"
        @click="onOpenChangeLocationModal"
      ></i>

      <i
        class="pi pi-trash cursor-pointer text-sm text-red-500 transition-colors"
        @click="onOpenDeleteLocationModal"
      ></i>
    </div>
  </div>

  <GMapMap
    v-if="field?.settings?.coordinates?.length === 2 && address"
    class="mt-2"
    :center="coordinates"
    :style="{ height: '250px' }"
    :zoom="13"
  >
    <GMapMarker
      icon="https://maps.google.com/mapfiles/ms/icons/green-dot.png"
      :position="coordinates"
    />
  </GMapMap>

  <p
    v-if="!address"
    class="cursor-pointer text-sm text-sky-500 hover:text-sky-600"
    @click="onOpenChangeLocationModal"
  >
    {{
      props.field?.settings?.editLocation
        ? $t(`components.forms.title.${address ? 'changeLocation' : 'addLocation'}`)
        : ''
    }}
  </p>
</template>
